import React, {
   MutableRefObject,
   ReactNode,
   useCallback,
   useRef,
} from 'react';
import { formatMessageBundle } from '@unifocus/js-utils';
import { promiseDebounce } from '../../../../utils/PromiseDebounce';
import { FormApi, SubmissionErrors, useForm } from '../../form';
import { Queue } from '../../../../utils/Queue';
import { ActiveAndDirtyFieldDetector } from './ActiveAndDirtyFieldDetector';
import messages from '../../../../localized-messages';

const DEBOUNCE_MS = 750;

export interface AutoSaveHandlerProps<T = Record<string, any>> {

   initialValues?: T;

   onSave?: (fieldNames: Array<keyof T>, values: T, form: FormApi<T>) =>
      SubmissionErrors
      | Promise<SubmissionErrors | undefined>
      | undefined
      | void;

   children?: ReactNode;
}

export const AutoSaveHandler = <T, >(props: AutoSaveHandlerProps<T>) => {

   const form = useForm<T,Partial<T>>();
   const queuedDataRef = useRef<Queue<keyof T>>(new Queue<keyof T>());
   const previouslySavedValuesRef = useRef<T | undefined>(props.initialValues || {} as T);
   const processRequestsRef = useRef(runRequest(props.onSave)(form, queuedDataRef, previouslySavedValuesRef));

   const processRequests = processRequestsRef.current;

   const onActiveFieldChange = useCallback((oldActiveField: keyof T)=>{

      // A blur occurred, we left oldActiveField for a new one
      const fieldToSave = oldActiveField;
      if (shouldSaveValue(form, fieldToSave as string)) {
         queuedDataRef.current.push(fieldToSave);
         processRequests().then(() => {
            // Force a recheck in case something gets missed, set the timeout a little above
            // the debounce amount
            setTimeout(processRequests, DEBOUNCE_MS + 100);
         });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   },[]);

   const onDirtyFieldChange = useCallback((fieldToSave: keyof T)=> {
      if (shouldSaveValue(form, fieldToSave as string)) {
         queuedDataRef.current.push(fieldToSave);
         processRequests().then(() => {
            const moreDirtyFields = Object.keys(form.getState().dirtyFields);
            if (moreDirtyFields.length > 0) {
               moreDirtyFields.forEach((fieldToSave) => {
                  onDirtyFieldChange(fieldToSave as keyof T);
               });
               return;
            }
            // Force a recheck in case something gets missed, set the timeout a little above
            // the debounce amount
            setTimeout(processRequests, DEBOUNCE_MS + 100);
         });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   },[]);

   return (<ActiveAndDirtyFieldDetector onActiveFieldChange={onActiveFieldChange} onDirtyFieldChange={onDirtyFieldChange}>
      {props.children}
   </ActiveAndDirtyFieldDetector>);
};

const shouldSaveValue = <T, >(form: FormApi<T, Partial<T>>,
                              fieldName: string): boolean => {

   const errors = form.getState().errors || {};
   const valueHasError = errors[fieldName] !== undefined;

   if (valueHasError) {
      // Value has error, don't save
      return false;
   }

   return (form.getState().dirtyFields[fieldName]);
};


const handleError = <T,>(form: FormApi<T, Partial<T>>, fieldsToSave: Array<keyof T>, previouslySavedValues: T, reject: (reason?: any) => void, reason: any) => {

   for (const fieldToSave of fieldsToSave) {
      const fieldState = form.getFieldState(fieldToSave);
      if (fieldState) {
         (form.getState().values as any)[fieldToSave] = previouslySavedValues[fieldToSave];
      }
      const saveErrors: any = {};
      saveErrors[fieldToSave] = formatMessageBundle(messages, "res_errorSavingField");
      form.mutators.setSaveErrors(saveErrors);
   }
   reject(reason);
};

const getValuesToSave = <T,>(fieldsToSave: Array<keyof T>, currentlySavedValues: T, formValues: T) => {

   const values = Object.assign({}, currentlySavedValues) as T;
   for (const fieldToSave of fieldsToSave) {
      values[fieldToSave] = formValues[fieldToSave];
   }
   return values;
};

export const runRequest = <T,>(onSave?: (fieldNames: Array<keyof T>, values: T, form: FormApi<T>) =>
   SubmissionErrors
   | Promise<SubmissionErrors | undefined>
   | undefined
   | void
) => {

   return (form: FormApi<T, Partial<T>>,
           fieldsToSaveQueue: MutableRefObject<Queue<keyof T>>,
           previouslySavedValuesRef: MutableRefObject<T | undefined>) => {

      return promiseDebounce(() => new Promise<void>((resolve, reject) => {

         const fieldsToSave = Array.from(new Set(fieldsToSaveQueue.current.toArray()));
         fieldsToSaveQueue.current.clear();
         if (fieldsToSave.length === 0) {
            resolve();
            return;
         }

         form.mutators.setFormSubmitting(true);
         form.mutators.clearSubmitErrors(fieldsToSave);

         const values = form.getState().values;

         const valuesToSave = getValuesToSave(fieldsToSave,
                                              previouslySavedValuesRef.current || {} as T,
                                              values);

         const promise = onSave ? onSave(fieldsToSave,
                                valuesToSave,
                                form) : undefined;

         if (promise && (promise as any).then) {
            promise.then((response: any) => {
               previouslySavedValuesRef.current = valuesToSave;
               for (const fieldToSave of fieldsToSave) {
                  form.mutators.setInitialValue(fieldToSave, valuesToSave[fieldToSave]);
               }
               form.mutators.setSaveErrors(response);
               form.mutators.setFormSubmitting(false);
               resolve(response);
               return response;
            }).catch((reason: any) => {
               handleError(form,
                           fieldsToSave,
                           previouslySavedValuesRef.current || {} as T,
                           reject,
                           reason);
            });
         }
         else {
            try {
               const response = promise as any;
               previouslySavedValuesRef.current = valuesToSave;
               for (const fieldToSave of fieldsToSave) {
                  form.mutators.setInitialValue(fieldToSave, valuesToSave[fieldToSave]);
               }
               form.mutators.setSaveErrors(response);
               form.mutators.setFormSubmitting(false);
               resolve(response);
            }
            catch (error) {
               handleError(form,
                           fieldsToSave,
                           previouslySavedValuesRef.current || {} as T,
                           reject,
                           error);
            }
         }
      }), DEBOUNCE_MS);
   };
};
